<template>
  <div
    class="fixed top-0 left-0 w-full h-full z-[12345678900] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm p-4"
    @click="handleCancel"
  >
    <div
      class="bg-white w-full max-w-[440px] rounded-2xl shadow-xl border border-slate-100 overflow-hidden"
      @click.stop
    >
      <div
        class="px-6 py-2.5 flex items-center justify-between border-b border-slate-50"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
              />
            </svg>
          </div>
          <h2 class="text-lg font-bold text-slate-800">
            {{ $t("addPlayerToFreebet.title") }}
          </h2>
        </div>
        <button
          @click="handleCancel"
          class="text-slate-400 hover:text-slate-600"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="p-6">
        <div v-if="!isUploadingNewPlayer" class="space-y-6">
          <div class="space-y-2">
            <p
              class="text-[11px] font-bold text-slate-400 uppercase tracking-wider"
            >
              {{ $t("addPlayerToFreebet.freebetCampaign") }}
            </p>
            <div
              class="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center gap-3"
            >
              <div
                class="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center text-white"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>
              <span class="font-semibold text-slate-700">{{
                detail?.title
              }}</span>
            </div>
          </div>

          <div class="space-y-2">
            <p
              class="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {{ $t("addPlayerToFreebet.playerId") }}
            </p>
            <p class="text-xs text-slate-500 italic">
              {{ $t("addPlayerToFreebet.playerIdAlert") }}
            </p>

            <input
              type="text"
              v-model="formData.player_id"
              placeholder="Enter Player ID"
              class="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all custom-input text-black"
              @input="
                formData.player_id = formData.player_id.replace(/\s/g, '')
              "
            />
          </div>
        </div>

        <div
          v-if="isUploadingNewPlayer"
          class="py-12 flex flex-col items-center"
        >
          <Loader />
        </div>

        <div v-if="!isUploadingNewPlayer" class="mt-8 flex gap-3">
          <button
            @click="handleCancel"
            class="flex-1 py-2 font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
          >
            {{ $t("util.cancel") }}
          </button>
          <button
            @click="handleCreate"
            :disabled="!formData.player_id.trim()"
            class="flex-1 py-2 font-semibold text-white bg-[#386f99] disabled:bg-slate-300 rounded-xl transition-all flex items-center justify-center gap-2"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Add Player
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { postFetch } from "@/api/axiosClient";
import Loader from "./Loader.vue";

export default {
  name: "AddPlayerToFreebet",
  props: {
    detail: {
      type: Object,
    },
  },
  components: {
    Loader,
  },
  data() {
    return {
      formData: {
        player_id: "",
      },
      isUploadingNewPlayer: false,
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
  },
  mounted() {
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
  methods: {
    resetForm() {
      this.formData.player_id = "";
    },
    async handleCreate() {
      if (!this.formData.player_id.trim()) return;

      this.isUploadingNewPlayer = true;

      try {
        await postFetch(`freebets/restricted/${this.detail?.external_id}`, {
          player_id: this.formData.player_id,
        });

        await Swal.fire({
          title: "Success!",
          text: "Player added successfully to freebet campaign",
        });

        window.location.reload();
      } catch (error) {
        console.error("Errore durante l'aggiornamento:", error);

        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to add the player to freebet.";

        await Swal.fire({
          title: "Error",
          text: errorMsg,
          icon: "error",
        });
      } finally {
        this.isUploadingNewPlayer = false;
      }
    },
    handleCancel() {
      this.$emit("add-player-modal-open");
      this.resetForm();
    },
  },
};
</script>
